import { createSlice, current } from '@reduxjs/toolkit';
import { updateFreeShippingProgressBar } from '@services/cart';
import { checkForNonRxDiscount } from '../../utils/shopify-helpers';

export const cartSlice = createSlice({
	name: 'cart',
	initialState: {
		isLoading: false,
		loadingKey: null,
	},
	reducers: {
		setOthersGroupKey: (state, { payload }) => ({ ...state, othersKey: payload }),
		setCartInfo: (
			state,
			{
				payload: {
					currency,
					item_count,
					items,
					items_subtotal_price,
					original_total_price,
					requires_shipping,
					total_discount,
					total_price,
					total_weight,
				},
			}
		) => ({
			...state,
			currency,
			item_count,
			items,
			items_subtotal_price,
			original_total_price,
			requires_shipping,
			total_discount,
			total_price,
			total_weight,
		}),
		setIsLoading: (state, { payload: { value, key } }) => ({
			...state,
			loadingKey: key,
			isLoading: value,
		}),
		addItem: (state, { payload: { newItem, discountPrice = false } }) => {
			const newItems = [...state.items];
			const oldItemIndex = newItems.findIndex(item => item.key === newItem.key);

			if (oldItemIndex >= 0) {
				newItems.splice(oldItemIndex, 1);
			}

			// we need to account for adding/deleting the Blue Light Upsell for Non-Rx Frames. Does not apply for Sun Lenses
			const priceAdjustment = discountPrice ? 0 : newItem.price;

			window.pageContext.localization.country === 'Canada' &&
				updateFreeShippingProgressBar(state.total_price + priceAdjustment);

			return {
				...state,
				item_count: state.item_count + 1,
				total_price: state.total_price + priceAdjustment,
				items: [...newItems, newItem],
				isLoading: false,
				loadingKey: null,
			};
		},
		deleteItemByKey: (state, { payload: { itemKey, discountPrice = false } }) => {
			const newItems = [...state.items];
			const indexOfItem = newItems.findIndex(originalItem => originalItem.key === itemKey);
			const deletedItem = newItems[indexOfItem];
			const quantity = deletedItem?.quantity ?? 1;
			newItems.splice(indexOfItem, 1);

			// we need to account for adding/deleting the Blue Light Upsell for Non-Rx Frames. Does not apply for Sun Lenses
			const priceAdjustment = discountPrice ? 0 : deletedItem.price * quantity;

			window.pageContext.localization.country === 'Canada' &&
				updateFreeShippingProgressBar(state.total_price - priceAdjustment);

			return {
				...state,
				item_count: state.item_count - quantity,
				total_price: state.total_price - priceAdjustment,
				items: newItems,
				isLoading: false,
				loadingKey: null,
			};
		},
		deleteItemsBulk: (state, { payload: { deletedItems, nonRxGroup = false } }) => {
			const itemKeys = deletedItems.map(item => item.key);
			const newItems = [...state.items];
			let totalPrice = state.total_price;
			let itemCount = state.item_count;
			state.items.forEach(item => {
				if (itemKeys.includes(item.key)) {
					const deletedIndex = newItems.findIndex(originalItem => originalItem.key === item.key);
					const priceAdjustment = checkForNonRxDiscount(item.handle, nonRxGroup) ? 0 : item.final_price;
					newItems.splice(deletedIndex, 1);
					totalPrice -= priceAdjustment;
					itemCount -= 1;
				}
			});

			window.pageContext.localization.country === 'Canada' && updateFreeShippingProgressBar(totalPrice);

			return {
				...state,
				isLoading: false,
				loadingKey: null,
				items: newItems,
				item_count: itemCount,
				total_price: totalPrice,
			};
		},
		setPairCareProduct: (state, { payload: pairCareProduct }) => ({
			...state,
			pairCareProduct,
		}),
	},
});

export const { setCartInfo, setIsLoading, deleteItemByKey, deleteItemsBulk, addItem, setOthersGroupKey, setPairCareProduct } =
	cartSlice.actions;

export default cartSlice.reducer;
